import { Injectable, ChangeDetectorRef } from '@angular/core';
import { CsCardListComponent } from 'components/cs-card-list/cs-card-list';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { getCardListGap, getCardTransitionMs } from './browser-utils';
import { computeExerciseBadgeNumbers } from './exercise-badge-numbers';
import { comboSetPosition, joinComboSet, settleComboSetsAfterDrag } from './combo-set-rules';

/**
 * Configuration for drag-drop behavior
 */
export interface CardListDragConfig {
  cardSelector: string;                    // e.g., 'cs-food-card.ingredient-item'
  topBoundSelector?: string;               // e.g., 'cs-card-list-row.ingredient-list-header'
  bottomBoundSelector?: string;            // e.g., '[footer-cards]'

  // Element references
  hostElement: HTMLElement;                // Component's nativeElement
  listComponent: CsCardListComponent;      // For updatePositions()
  changeDetector: ChangeDetectorRef;       // For triggering change detection

  // Callbacks
  onReorder: (fromIndex: number, toIndex: number) => void;
  onComboSetDrop?: (draggedIndex: number, targetIndex: number) => void;
  onDragStart?: () => void;
  onDragEnd?: (draggedFinalIndex: number) => void;
  onComboSetTargetChange?: (targetIndex: number | null) => void;

  // Optional: data items corresponding to each card (by template index).
  // When provided, the drag service updates `.number-text` badge labels
  // after each swap to reflect the current visual order.
  badgeItems?: { comboSetNumber: number | undefined }[];
}

interface DragState {
  active: boolean;
  config: CardListDragConfig | null;
  draggingElement: HTMLElement | null;
  draggingIndex: number;
  originalIndex: number;  // starting index in cardPositions (for visual tracking)
  modelOriginalIndex: number;  // Angular $index (for onReorder callback)
  modelIndexOffset: number;  // modelOriginalIndex - originalIndex (to convert back)
  lastY: number;  // finger Y from previous frame (for delta calculation)
  cardPositions: { element: HTMLElement; top: number; height: number; index: number }[];
  comboSetTargetIndex: number | null;  // index of the card being hovered for combo set creation
}

/**
 * Reusable service for drag-and-drop functionality in card lists.
 * Handles long-press detection, auto-scroll, bounds clamping, and swap detection.
 *
 * Usage:
 * 1. Create a CardListDragConfig with your selectors and callbacks
 * 2. Call startLongPress() on pointerdown (mobile) or startDragHandle() from drag handle
 * 3. Use isDragging() for .dragging class binding
 * 4. Call destroy() in ngOnDestroy
 */
@Injectable()
export class CardListDragService {
  // Drag state
  private dragState: DragState = {
    active: false,
    config: null,
    draggingElement: null,
    draggingIndex: -1,
    originalIndex: -1,
    modelOriginalIndex: -1,
    modelIndexOffset: 0,
    lastY: 0,
    cardPositions: [],
    comboSetTargetIndex: null
  };

  // Flag to suppress click events after drag ends
  private dragJustEnded = false;

  // Bound event handlers for cleanup
  private boundOnDragMove: (e: PointerEvent) => void;
  private boundOnDragEnd: (e: PointerEvent) => void;
  private boundPreventScroll: (e: TouchEvent) => void;

  // Auto-scroll state
  private autoScrollInterval: ReturnType<typeof setInterval> | null = null;
  private scrollableElement: HTMLElement | null = null;
  private readonly AUTO_SCROLL_EDGE_SIZE = 80; // px from viewport edge to trigger scroll
  private readonly AUTO_SCROLL_SPEED = 15; // px per frame
  private currentScrollDirection = 0;
  private currentScrollSpeed = 0;

  // Long press state
  private longPressTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingDragEvent: PointerEvent | null = null;
  private pendingDragIndex: number = -1;
  private pendingConfig: CardListDragConfig | null = null;
  private longPressStartX: number = 0;
  private longPressStartY: number = 0;
  private wasLongPress = false;
  private readonly LONG_PRESS_MOVE_THRESHOLD = 10; // pixels - allow small touch jitter

  constructor() {
    this.boundOnDragMove = this.onDragMove.bind(this);
    this.boundOnDragEnd = this.onDragEnd.bind(this);
    this.boundPreventScroll = (e: TouchEvent) => e.preventDefault();
  }

  /**
   * Start long-press drag detection (for mobile - requires 300ms hold)
   */
  startLongPress(event: PointerEvent, index: number, config: CardListDragConfig): void {
    // on desktop, only allow drag via drag handle
    if (!hasClientSmallScreen()) return;

    // prevent default to stop browser text selection behavior
    event.preventDefault();

    // store pending drag info and initial position for movement threshold
    this.pendingDragEvent = event;
    this.pendingDragIndex = index;
    this.pendingConfig = config;
    this.wasLongPress = true;
    this.longPressStartX = event.clientX;
    this.longPressStartY = event.clientY;

    // disable text selection immediately to prevent selection during long press
    config.hostElement.classList.add('is-pressing');
    window.getSelection()?.removeAllRanges();

    // use long press delay (300ms)
    this.longPressTimeout = setTimeout(() => {
      this.activateDrag();
    }, 300);

    // add temporary listeners to cancel long press if user moves too much or releases
    document.addEventListener('pointermove', this.onLongPressMove);
    document.addEventListener('pointerup', this.cancelLongPress);
    document.addEventListener('pointercancel', this.cancelLongPress);
  }

  /**
   * Start immediate drag from drag handle (for desktop - no long press delay)
   */
  startDragHandle(event: PointerEvent, index: number, config: CardListDragConfig): void {
    event.preventDefault();
    event.stopPropagation();

    // store pending drag info and activate immediately
    this.pendingDragEvent = event;
    this.pendingDragIndex = index;
    this.pendingConfig = config;
    this.wasLongPress = false;
    this.activateDrag();
  }

  /**
   * Check if an item at the given model index is currently being dragged
   */
  isDragging(index: number): boolean {
    if (!this.dragState.active) return false;
    return (this.dragState.draggingIndex + this.dragState.modelIndexOffset) === index;
  }

  /**
   * Check if any drag is currently active
   */
  isAnyDragActive(): boolean {
    return this.dragState.active;
  }

  /**
   * Check if a drag just ended (for suppressing click events)
   */
  didDragJustEnd(): boolean {
    return this.dragJustEnded;
  }

  /**
   * Clean up all listeners and state (call in ngOnDestroy)
   */
  destroy(): void {
    this.cancelLongPress();
    this.stopAutoScroll();

    document.removeEventListener('pointermove', this.boundOnDragMove);
    document.removeEventListener('pointerup', this.boundOnDragEnd);
    document.removeEventListener('pointercancel', this.boundOnDragEnd);
    document.removeEventListener('touchmove', this.boundPreventScroll);

    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
      this.longPressTimeout = null;
    }
  }

  // Only cancel long press if movement exceeds threshold (allows touch jitter)
  private onLongPressMove = (event: PointerEvent) => {
    const dx = event.clientX - this.longPressStartX;
    const dy = event.clientY - this.longPressStartY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance > this.LONG_PRESS_MOVE_THRESHOLD) {
      this.cancelLongPress();
    }
  };

  private cancelLongPress = () => {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
      this.longPressTimeout = null;
    }

    // re-enable text selection since drag was cancelled
    if (this.pendingConfig) {
      this.pendingConfig.hostElement.classList.remove('is-pressing');
    }

    this.pendingDragEvent = null;
    this.pendingDragIndex = -1;
    this.pendingConfig = null;

    document.removeEventListener('pointermove', this.onLongPressMove);
    document.removeEventListener('pointerup', this.cancelLongPress);
    document.removeEventListener('pointercancel', this.cancelLongPress);
  };

  private activateDrag(): void {
    const event = this.pendingDragEvent;
    const index = this.pendingDragIndex;
    const config = this.pendingConfig;

    // clean up long press listeners
    document.removeEventListener('pointermove', this.onLongPressMove);
    document.removeEventListener('pointerup', this.cancelLongPress);
    document.removeEventListener('pointercancel', this.cancelLongPress);
    this.longPressTimeout = null;

    if (!event || index < 0 || !config) return;

    event.preventDefault();
    event.stopPropagation();

    // find the card element using the configured selector
    const cardElement = (event.target as HTMLElement).closest(config.cardSelector) as HTMLElement;
    if (!cardElement) return;

    // capture pointer for this element
    (event.target as HTMLElement).setPointerCapture(event.pointerId);

    // capture all card positions for hit testing
    const cardPositions: DragState['cardPositions'] = [];
    const cards = config.hostElement.querySelectorAll(config.cardSelector);
    cards.forEach((card: Element, i: number) => {
      const htmlCard = card as HTMLElement;
      const cardTransform = htmlCard.style.transform;
      const cardMatch = cardTransform.match(/translate\(([^,]+),\s*([^)]+)\)/);
      const top = cardMatch ? parseFloat(cardMatch[2]) : 0;
      cardPositions.push({
        element: htmlCard,
        top,
        height: htmlCard.offsetHeight,
        index: i
      });
    });

    // find the dragged card's actual index in the querySelectorAll result
    // this may differ from the Angular $index when some items don't render as cards
    // (e.g., ingredients with missing food data are conditionally excluded from the DOM)
    let cardIndex = index;
    for (let i = 0; i < cardPositions.length; i++) {
      if (cardPositions[i].element === cardElement) {
        cardIndex = i;
        break;
      }
    }

    this.dragState = {
      active: true,
      config,
      draggingElement: cardElement,
      draggingIndex: cardIndex,
      originalIndex: cardIndex,
      modelOriginalIndex: index,
      modelIndexOffset: index - cardIndex,
      lastY: event.clientY,
      cardPositions,
      comboSetTargetIndex: null
    };

    // disable transitions on dragged element immediately to prevent animation stutter
    cardElement.style.transition = 'none';
    cardElement.style.zIndex = '1000';

    // add document-level listeners
    document.addEventListener('pointermove', this.boundOnDragMove);
    document.addEventListener('pointerup', this.boundOnDragEnd);
    document.addEventListener('pointercancel', this.boundOnDragEnd);

    // enable smooth animations on non-dragged cards during reorder
    // and tell cs-card-list which element is being dragged so it skips it in updatePositions()
    if (config.listComponent) {
      config.listComponent.allowDragAnimations = true;
      config.listComponent.draggingElement = cardElement;
    }

    // switch from is-pressing to is-dragging to start wobble animation
    // wobble animation delays are handled via CSS :nth-child rules in _placeholders.scss
    config.hostElement.classList.remove('is-pressing');
    config.hostElement.classList.add('is-dragging');

    // prevent page scroll during drag on mobile
    document.addEventListener('touchmove', this.boundPreventScroll, { passive: false });

    // brief pop animation on long-press activation to confirm drag started
    if (this.wasLongPress) {
      cardElement.style.scale = '1.03';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          cardElement.style.transition = 'scale 0.15s ease-out';
          cardElement.style.scale = '1';
          setTimeout(() => {
            // clean up scale styles after animation completes
            cardElement.style.transition = 'none';
            cardElement.style.scale = '';
          }, 150);
        });
      });
    }

    // call optional onDragStart callback
    config.onDragStart?.();

    config.changeDetector.markForCheck();

    // clear pending state
    this.pendingDragEvent = null;
    this.pendingDragIndex = -1;
    this.pendingConfig = null;
  }

  private onDragMove(event: PointerEvent): void {
    if (!this.dragState.draggingElement || !this.dragState.config) return;

    const config = this.dragState.config;

    event.preventDefault();
    window.getSelection()?.removeAllRanges();

    // delta-based positioning: read current position, add finger movement delta
    const transform = this.dragState.draggingElement.style.transform;
    const match = transform.match(/translate\(([^,]+),\s*([^)]+)\)/);
    const currentX = match ? match[1] : '0px';
    const currentTransformY = match ? parseFloat(match[2]) : 0;

    // calculate how much finger moved since last frame
    const deltaY = event.clientY - this.dragState.lastY;
    this.dragState.lastY = event.clientY;

    // add delta to current position and clamp
    let newY = currentTransformY + deltaY;
    newY = this.clampToBounds(newY, config);

    // IMMEDIATELY update dragged element position (no Angular, no blocking)
    // this ensures responsive visual feedback before any swap logic
    this.dragState.draggingElement.style.transform = `translate(${currentX}, ${newY}px)`;
    this.dragState.draggingElement.style.transition = 'none';
    this.dragState.draggingElement.style.zIndex = '1000';

    // handle auto-scroll when dragging near viewport edges
    this.handleAutoScroll(event.clientY);

    // reorder swaps first (cards move out of the way), then check combo set overlap
    this.detectSwaps(newY);

    if (this.dragState.config?.onComboSetDrop) {
      this.detectComboSetTarget(newY);
    }
  }

  /**
   * Get the current combo set target index (the card being hovered for merge)
   */
  getComboSetTargetIndex(): number | null {
    return this.dragState.comboSetTargetIndex;
  }

  /**
   * Get the current dragging index (visual position, may differ from model index after swaps)
   */
  getDraggingIndex(): number {
    return this.dragState.draggingIndex;
  }

  /**
   * Map a visual index back to the original model/template index.
   * During drag, cards swap visual positions but the DOM/template order stays fixed.
   * cardPositions[i] always refers to the DOM element at template index i,
   * but its .index field tracks the current visual position.
   */
  getModelIndex(visualIndex: number): number {
    for (let i = 0; i < this.dragState.cardPositions.length; i++) {
      if (this.dragState.cardPositions[i].index === visualIndex) {
        return i;
      }
    }
    return visualIndex;
  }

  /**
   * Extract swap detection into its own method (called from onDragMove when not in combo set mode)
   */
  private detectSwaps(newY: number): void {
    if (!this.dragState.draggingElement) return;

    const draggedTop = newY;
    const draggedOffsetHeight = this.dragState.draggingElement.offsetHeight;
    const draggedBottom = newY + draggedOffsetHeight;
    let newIndex = this.dragState.draggingIndex;

    for (const pos of this.dragState.cardPositions) {
      if (pos.index === this.dragState.draggingIndex) continue;

      const cardCenter = pos.top + (pos.height / 2);

      // if dragging down: swap when bottom edge passes other card's center
      if (this.dragState.draggingIndex < pos.index && draggedBottom > cardCenter) {
        newIndex = Math.max(newIndex, pos.index);
      }
      // if dragging up: swap when top edge passes other card's center
      else if (this.dragState.draggingIndex > pos.index && draggedTop < cardCenter) {
        newIndex = Math.min(newIndex, pos.index);
      }
    }

    // if position changed, update visuals directly (no Angular, no blocking)
    if (newIndex !== this.dragState.draggingIndex) {
      const oldIndex = this.dragState.draggingIndex;
      const gap = getCardListGap();
      const draggedCardPos = this.dragState.cardPositions.find(p => p.index === oldIndex);
      // if DOM was invalidated (e.g. by a sync-triggered re-render), skip swap
      if (!draggedCardPos) return;
      const draggedHeight = draggedCardPos.height;

      // directly update transforms for affected cards (no change detection)
      if (oldIndex < newIndex) {
        // dragging down: cards between shift UP
        for (const pos of this.dragState.cardPositions) {
          if (pos.index > oldIndex && pos.index <= newIndex) {
            pos.top -= (draggedHeight + gap);
            const currentTransform = pos.element.style.transform;
            const xMatch = currentTransform.match(/translate\(([^,]+),/);
            const currentX = xMatch ? xMatch[1] : '0px';
            pos.element.style.transform = `translate(${currentX}, ${pos.top}px)`;
          }
        }
      } else {
        // dragging up: cards between shift DOWN
        for (const pos of this.dragState.cardPositions) {
          if (pos.index >= newIndex && pos.index < oldIndex) {
            pos.top += (draggedHeight + gap);
            const currentTransform = pos.element.style.transform;
            const xMatch = currentTransform.match(/translate\(([^,]+),/);
            const currentX = xMatch ? xMatch[1] : '0px';
            pos.element.style.transform = `translate(${currentX}, ${pos.top}px)`;
          }
        }
      }

      // update indices in cardPositions to match new visual order
      for (const pos of this.dragState.cardPositions) {
        if (pos.index === oldIndex) {
          pos.index = newIndex;
        } else if (oldIndex < newIndex && pos.index > oldIndex && pos.index <= newIndex) {
          pos.index--;
        } else if (oldIndex > newIndex && pos.index >= newIndex && pos.index < oldIndex) {
          pos.index++;
        }
      }

      this.dragState.draggingIndex = newIndex;
      // model update (onReorder) deferred to drag end

      // update badge numbers to reflect current visual order
      this.updateDragPreview();
    }
  }

  /**
   * Build visual items array in current visual order (cloned from badgeItems).
   * Applies the combo set rules (combo-set-rules.ts) the drop will apply.
   */
  private buildVisualItems(): { comboSetNumber: number | undefined }[] | null {
    const items = this.dragState.config?.badgeItems;
    if (!items) return null;

    const positions = this.dragState.cardPositions;
    const visualItems: { comboSetNumber: number | undefined }[] = new Array(positions.length);
    for (const pos of positions) {
      const templateIdx = positions.indexOf(pos);
      visualItems[pos.index] = { comboSetNumber: items[templateIdx].comboSetNumber };
    }

    // if combo set target is active, simulate the merge (dragged + target share a group)
    const dragIdx = this.dragState.draggingIndex;
    const targetIdx = this.dragState.comboSetTargetIndex;
    const merged = targetIdx !== null
      ? joinComboSet(visualItems, dragIdx, targetIdx).map(comboSetNumber => ({ comboSetNumber }))
      : visualItems;

    // apply the same rules the drop applies, so the preview matches what the drop will produce
    return settleComboSetsAfterDrag(merged, dragIdx, targetIdx !== null).map(comboSetNumber => ({ comboSetNumber }));
  }

  /**
   * Update badge numbers, bracket classes, and combo set overlay on each card
   * to reflect current visual order. Uses direct DOM manipulation — no Angular CD.
   */
  private updateDragPreview(): void {
    const visualItems = this.buildVisualItems();
    if (!visualItems) return;

    const positions = this.dragState.cardPositions;
    const dragIdx = this.dragState.draggingIndex;

    // update badge numbers
    const numbers = computeExerciseBadgeNumbers(visualItems);
    for (const pos of positions) {
      const textEl = pos.element.querySelector('.number-text') as HTMLElement;
      if (textEl?.firstChild) {
        textEl.firstChild.nodeValue = numbers[pos.index];
      }
    }

    // determine if the dragged exercise is part of a combo set in the preview.
    // show overlay on all group members when:
    // - joining a new combo set (via overlap or gap auto-join)
    // - moving within an existing combo set (for consistent visual feedback)
    const draggedVisualCombo = visualItems[dragIdx]?.comboSetNumber;
    const isInComboSet = draggedVisualCombo !== undefined;

    // update bracket classes and combo set overlay — deferred to next frame so classList.toggle
    // doesn't force a style recalc that kills the CSS transition on transform
    requestAnimationFrame(() => {
      for (const pos of positions) {
        const item = visualItems[pos.index];
        const position = comboSetPosition(visualItems, pos.index);

        pos.element.classList.toggle('combo-set-first', position === 'first');
        pos.element.classList.toggle('combo-set-middle', position === 'middle');
        pos.element.classList.toggle('combo-set-last', position === 'last');

        // combo set overlay: show on all cards in the group being joined
        const showOverlay = isInComboSet && item.comboSetNumber === draggedVisualCombo;
        pos.element.classList.toggle('combo-set-target', showOverlay);
      }
      // also show overlay on the dragged element itself
      this.dragState.draggingElement?.classList.toggle('combo-set-target', isInComboSet);
    });
  }

  private clearComboSetTarget(): void {
    if (this.dragState.comboSetTargetIndex === null) return;

    this.dragState.comboSetTargetIndex = null;
    this.dragState.config?.onComboSetTargetChange?.(null);
  }

  /**
   * Check if dragged card significantly overlaps any other card.
   * Uses overlap ratio: the dragged card must cover >40% of the target card's height.
   */
  private detectComboSetTarget(draggedY: number): void {
    if (!this.dragState.config?.onComboSetDrop || !this.dragState.draggingElement) return;

    const config = this.dragState.config;
    const draggedHeight = this.dragState.draggingElement.offsetHeight;
    const draggedTop = draggedY;
    const draggedBottom = draggedY + draggedHeight;

    let newTarget: number | null = null;

    for (const pos of this.dragState.cardPositions) {
      if (pos.index === this.dragState.draggingIndex) continue;

      const overlap = Math.max(0,
        Math.min(draggedBottom, pos.top + pos.height) - Math.max(draggedTop, pos.top));
      const overlapRatio = overlap / pos.height;

      if (overlapRatio > 0.2) {
        newTarget = pos.index;
        break;
      }
    }

    if (newTarget !== null && newTarget !== this.dragState.comboSetTargetIndex) {
      // activate new target — updateDragPreview handles overlay classes
      this.dragState.comboSetTargetIndex = newTarget;
      config.onComboSetTargetChange?.(newTarget);
      this.updateDragPreview();
    } else if (newTarget === null && this.dragState.comboSetTargetIndex !== null) {
      // overlap dropped — clear
      this.clearComboSetTarget();
      this.updateDragPreview();
    }
  }

  private clampToBounds(newY: number, config: CardListDragConfig): number {
    if (!config.listComponent || !this.dragState.draggingElement) return newY;

    const listElement = config.listComponent.elemRef.nativeElement;
    const cardHeight = this.dragState.draggingElement.offsetHeight;

    // find the top bound element
    let minY = 0;
    if (config.topBoundSelector) {
      const topElement = listElement.querySelector(config.topBoundSelector) as HTMLElement;
      if (topElement) {
        const topTransform = topElement.style.transform;
        const topMatch = topTransform.match(/translate\([^,]+,\s*([^)]+)\)/);
        const topY = topMatch ? parseFloat(topMatch[1]) : 0;
        minY = topY + topElement.offsetHeight + 12; // 12 = card gap
      }
    }

    // find the bottom bound element
    let maxY = listElement.offsetHeight - cardHeight;
    if (config.bottomBoundSelector) {
      // support comma-separated selectors (e.g., '.footer, .validation')
      const selectors = config.bottomBoundSelector.split(',').map(s => s.trim());
      for (const selector of selectors) {
        const bottomElement = listElement.querySelector(selector) as HTMLElement;
        if (bottomElement) {
          const bottomTransform = bottomElement.style.transform;
          const bottomMatch = bottomTransform.match(/translate\([^,]+,\s*([^)]+)\)/);
          const bottomY = bottomMatch ? parseFloat(bottomMatch[1]) : listElement.offsetHeight;
          maxY = bottomY - cardHeight - 12;
          break;
        }
      }
    }

    return Math.max(minY, Math.min(maxY, newY));
  }

  private handleAutoScroll(clientY: number): void {
    const config = this.dragState.config;
    if (!config) return;

    // find the scrollable element if not cached
    if (!this.scrollableElement) {
      this.scrollableElement = config.hostElement.closest('cs-content')?.querySelector('.scroll-area') as HTMLElement;
    }
    if (!this.scrollableElement) return;

    const viewportHeight = window.innerHeight;
    const distanceFromTop = clientY;
    const distanceFromBottom = viewportHeight - clientY;

    // update scroll direction and speed
    this.currentScrollDirection = 0;
    this.currentScrollSpeed = 0;

    // if near top edge, scroll up
    if (distanceFromTop < this.AUTO_SCROLL_EDGE_SIZE) {
      this.currentScrollDirection = -1;
      this.currentScrollSpeed = this.AUTO_SCROLL_SPEED * (1 - distanceFromTop / this.AUTO_SCROLL_EDGE_SIZE);
    }
    // if near bottom edge, scroll down
    else if (distanceFromBottom < this.AUTO_SCROLL_EDGE_SIZE) {
      this.currentScrollDirection = 1;
      this.currentScrollSpeed = this.AUTO_SCROLL_SPEED * (1 - distanceFromBottom / this.AUTO_SCROLL_EDGE_SIZE);
    }

    // if in an edge zone, start auto-scrolling
    if (this.currentScrollDirection !== 0 && !this.autoScrollInterval) {
      const scrollElement = this.scrollableElement;
      this.autoScrollInterval = setInterval(() => {
        const scrollBefore = scrollElement.scrollTop;
        const intendedScrollAmount = this.currentScrollDirection * Math.max(this.currentScrollSpeed, 2);
        scrollElement.scrollTop += intendedScrollAmount;
        const actualScrollAmount = scrollElement.scrollTop - scrollBefore;

        // compensate card position so it stays at same viewport position during scroll
        if (this.dragState.draggingElement && this.dragState.config) {
          const transform = this.dragState.draggingElement.style.transform;
          const match = transform.match(/translate\(([^,]+),\s*([^)]+)\)/);
          const currentX = match ? match[1] : '0px';
          const currentY = match ? parseFloat(match[2]) : 0;

          // move card by scroll amount to keep it at same viewport position
          let newY = currentY + actualScrollAmount;
          newY = this.clampToBounds(newY, this.dragState.config);
          this.dragState.draggingElement.style.transform = `translate(${currentX}, ${newY}px)`;
        }
      }, 16); // ~60fps
    }
    // if not in edge zone, stop auto-scrolling
    else if (this.currentScrollDirection === 0 && this.autoScrollInterval) {
      this.stopAutoScroll();
    }
  }

  private stopAutoScroll(): void {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
    this.scrollableElement = null;
  }

  private onDragEnd(event: PointerEvent): void {
    if (!this.dragState.draggingElement || !this.dragState.config) return;

    const config = this.dragState.config;

    // stop auto-scroll
    this.stopAutoScroll();

    // release pointer capture
    try {
      (event.target as HTMLElement).releasePointerCapture(event.pointerId);
    } catch {
      // ignore if pointer wasn't captured
    }

    // store reference before resetting state
    const draggedElement = this.dragState.draggingElement;

    // reset element styles
    draggedElement.style.zIndex = '';

    // remove document listeners
    document.removeEventListener('pointermove', this.boundOnDragMove);
    document.removeEventListener('pointerup', this.boundOnDragEnd);
    document.removeEventListener('pointercancel', this.boundOnDragEnd);
    document.removeEventListener('touchmove', this.boundPreventScroll);

    // determine action: combo set merge vs reorder
    // convert card indices back to model indices for the callback
    // (card indices may differ from model indices when some items don't render as cards)
    const finalIndex = this.dragState.draggingIndex;
    const originalIndex = this.dragState.originalIndex;
    const offset = this.dragState.modelIndexOffset;
    const comboSetTarget = this.dragState.comboSetTargetIndex;

    // clean up combo set overlay classes from all cards
    for (const pos of this.dragState.cardPositions) {
      pos.element.classList.remove('combo-set-target');
    }
    draggedElement.classList.remove('combo-set-target');

    if (comboSetTarget !== null) {
      this.clearComboSetTarget();
    }

    if (comboSetTarget !== null && config.onComboSetDrop) {
    // if reorder happened during drag, apply it first
      if (finalIndex !== originalIndex) {
        config.onReorder(originalIndex + offset, finalIndex + offset);
      }
      config.onComboSetDrop(finalIndex + offset, comboSetTarget + offset);
    } else if (finalIndex !== originalIndex) {
      config.onReorder(originalIndex + offset, finalIndex + offset);
    }

    // call optional onDragEnd callback with the dragged exercise's final position
    config.onDragEnd?.(finalIndex);

    // reset drag state
    this.dragState = {
      active: false,
      config: null,
      draggingElement: null,
      draggingIndex: -1,
      originalIndex: -1,
      modelOriginalIndex: -1,
      modelIndexOffset: 0,
      lastY: 0,
      cardPositions: [],
      comboSetTargetIndex: null
    };

    // trigger cs-card-list to recalculate positions
    if (config.listComponent) {
      // keep z-index high during end animation so card stays on top
      draggedElement.style.zIndex = '1000';
      draggedElement.style.transition = '';

      requestAnimationFrame(() => {
        config.listComponent.draggingElement = null;
        config.listComponent.updatePositions();
        config.listComponent.allowDragAnimations = false;

        // reset z-index after transition completes
        setTimeout(() => {
          draggedElement.style.zIndex = '';
        }, getCardTransitionMs());
      });
    }

    // remove is-dragging class
    config.hostElement.classList.remove('is-dragging');

    // suppress click events briefly after drag ends
    this.dragJustEnded = true;
    setTimeout(() => {
      this.dragJustEnded = false;
    }, 100);

    config.changeDetector.markForCheck();
  }
}
